import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adbApi } from '@/lib/api';
import { useDevice } from '@/store/device';
import { useAuth } from '@/store/auth';
import { KvCard, KvList } from '@/components/KvCard';
import { GroupSection } from '@/components/GroupSection';
import { ExportDeviceFingerprint } from '@/components/ExportDeviceFingerprint';
import type { FingerprintSnapshot } from '@/lib/exportFingerprint';
import {
  Activity,
  BatteryFull,
  Bluetooth,
  Camera,
  ChevronRight,
  Clock,
  Cpu,
  Flame,
  Gauge,
  Globe2,
  HardDrive,
  Layers,
  ListTree,
  Monitor,
  Network,
  PowerOff,
  Radar,
  Radio,
  RefreshCw,
  Server,
  Shield,
  ShieldCheck,
  Signal,
  Smartphone,
  TerminalSquare,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import clsx from 'clsx';

interface CardSpec {
  id: string;
  title: string;
  /**
   * One or more catalog command names that feed this card. Results are
   * shallow-merged before flattening (later entries win on key collision).
   * Most cards use a single command; "运行时 / 系统" and "私有 DNS / 代理"
   * combine two cheap calls so a single visual unit answers a single
   * conceptual question.
   */
  cmds: string[];
  icon: React.ComponentType<{ className?: string }>;
  /** Set when the card needs a special render (e.g. CPU shows a perf-page link). */
  variant?: 'cpu' | 'top';
  /**
   * Explicit horizontal column span (1 / 2 / 3) overriding the auto-pick
   * based on field count. Use this for cards whose visual weight is known
   * (e.g. storage always wants 3 cols because mounts list is long; thermal
   * always wants 1 because it's typically empty). Cards without an
   * override fall back to `pickSpan(entries)`.
   */
  span?: 1 | 2 | 3;
}

interface GroupSpec {
  id: string;
  /** Visible heading. */
  title: string;
  /** Tailwind text-color class for icon + count pill. */
  accent: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Optional one-liner shown in the header. */
  subtitle?: string;
  cards: CardSpec[];
}

/**
 * 7-group / 22-card device overview. Each group corresponds to a logical
 * dimension of the device — hardware identity, OS configuration, network
 * stack, etc. — and groups together the adb-toolbox commands that answer
 * questions about that dimension. The grouping is what makes a 22-card
 * page navigable; without it, a flat list overwhelms users on first view.
 *
 * Card design rules (unchanged from the v1 flat layout):
 *   - One conceptual question → one card. Combine commands only when the
 *     answer requires merging fields from multiple sources (e.g.
 *     "运行时" merges build + runtime + locale).
 *   - Each command must return a flat-ish object; nested objects are
 *     summarised by `flatten()` as `{n keys}` so cards stay skim-friendly.
 *   - Avoid commands that need arguments (e.g. `perf.meminfo` needs a
 *     `package`) — those belong on the Commands page.
 */
// Each card declares an explicit `span` so the grid stays predictable
// regardless of which fields a particular ROM happens to emit. Card order
// within a group is also tuned: a wide card (span 3) at the head followed
// by short cards lets `grid-flow-row-dense` fill the second row tightly,
// keeping each row's bottom edge aligned. Without the explicit ordering,
// a 1-2-1-3 sequence would leave a half-empty row mid-group.
const GROUPS: GroupSpec[] = [
  {
    id: 'hardware',
    title: '硬件 · Hardware',
    subtitle: '设备身份 · CPU · GPU · 内存 · 屏幕 · 传感器 · 相机',
    accent: 'text-emerald-500',
    icon: Smartphone,
    cards: [
      { id: 'device', title: '设备 · Device', cmds: ['device.info'], icon: Smartphone, span: 3 },
      { id: 'cpu', title: 'CPU · Processor', cmds: ['device.cpu'], icon: Cpu, span: 2 },
      { id: 'gpu', title: 'GPU · Graphics', cmds: ['device.gpu'], icon: Layers, span: 1 },
      { id: 'memory', title: '内存 · Memory', cmds: ['device.memory'], icon: Server, span: 2 },
      { id: 'display', title: '屏幕 · Display', cmds: ['device.display'], icon: Monitor, span: 1 },
      { id: 'sensors', title: '传感器 · Sensors', cmds: ['device.sensors'], icon: Radar, span: 3 },
      { id: 'cameras', title: '相机 · Cameras', cmds: ['device.cameras'], icon: Camera, span: 3 },
    ],
  },
  {
    id: 'system',
    title: '系统 · System',
    subtitle: '内核 · 构建 · 运行时 · 区域 · 时间',
    accent: 'text-sky-500',
    icon: TerminalSquare,
    // Order: build (3) takes the full first row; runtime + locale + uptime
    // (1+1+1) line up on the second row.
    cards: [
      { id: 'build', title: '构建与启动 · Build', cmds: ['device.build'], icon: TerminalSquare, span: 3 },
      { id: 'runtime', title: '运行时 · Runtime', cmds: ['device.runtime'], icon: ShieldCheck, span: 1 },
      { id: 'locale', title: '区域 · Locale', cmds: ['device.locale'], icon: Globe2, span: 1 },
      { id: 'uptime', title: '运行时间 · Uptime', cmds: ['device.uptime', 'system.now'], icon: Clock, span: 1 },
    ],
  },
  {
    id: 'network',
    title: '网络 · Network',
    subtitle: '连通性 · Wi-Fi · 蜂窝 · 蓝牙 · 私有 DNS / 代理',
    accent: 'text-cyan-500',
    icon: Wifi,
    // Order: wifi (2) + connectivity (1) make row 1; cellular + bluetooth +
    // dnsProxy (1+1+1) line up on row 2 — a tidy 2+1 / 1+1+1 mosaic.
    cards: [
      { id: 'wifi', title: 'Wi-Fi · 状态', cmds: ['net.wifi.status'], icon: Wifi, span: 2 },
      { id: 'connectivity', title: '连通性 · Connectivity', cmds: ['net.connectivity'], icon: Network, span: 1 },
      { id: 'cellular', title: '蜂窝 · Cellular', cmds: ['net.cellular'], icon: Signal, span: 1 },
      { id: 'bluetooth', title: '蓝牙 · Bluetooth', cmds: ['net.bluetooth'], icon: Bluetooth, span: 1 },
      { id: 'dnsProxy', title: '私有 DNS · 代理', cmds: ['net.privateDns.get', 'net.proxy.get'], icon: ShieldCheck, span: 1 },
    ],
  },
  {
    id: 'battery',
    title: '电池与温度 · Battery & Thermal',
    subtitle: '健康度 · 功率 · 温度 · 电源',
    accent: 'text-amber-500',
    icon: BatteryFull,
    // Battery (2) + thermal (1) on row 1; power (1) drops to row 2 on its
    // own. We accept a partially-empty second row here because the
    // alternative (forcing power onto row 1) would mean splitting battery
    // into 1-col, where its 14 fields would tower instead.
    cards: [
      { id: 'battery', title: '电池 · Battery', cmds: ['device.battery'], icon: BatteryFull, span: 2 },
      { id: 'thermal', title: '温度 · Thermal', cmds: ['perf.thermal'], icon: Flame, span: 1 },
      { id: 'power', title: '电源状态 · Power', cmds: ['device.powerState'], icon: PowerOff, span: 1 },
    ],
  },
  {
    id: 'storage',
    title: '存储 · Storage',
    subtitle: '挂载 · 分区 · UFS / eMMC',
    accent: 'text-purple-500',
    icon: HardDrive,
    cards: [
      { id: 'storage', title: '存储 · Storage', cmds: ['device.storage'], icon: HardDrive, span: 3 },
    ],
  },
  {
    id: 'runtime',
    title: '运行时 · Runtime',
    subtitle: '前台 Activity · 全局 CPU · Top 进程',
    accent: 'text-indigo-500',
    icon: Activity,
    // CPU + top 进程 each pull ~5-7 weighty fields; pair them at span 2 on
    // row 1 (totalling 4 cols → flows to two rows with dense layout).
    // lifecycle (2 fields) goes last as span 1 — short, sits on row 2.
    cards: [
      { id: 'cpuload', title: 'CPU 占用 · Usage', cmds: ['perf.cpuinfo'], icon: Gauge, variant: 'cpu', span: 2 },
      { id: 'top', title: 'Top 进程 · Top 5', cmds: ['proc.top'], icon: ListTree, variant: 'top', span: 2 },
      { id: 'lifecycle', title: '前台 Activity', cmds: ['app.dumpActivity'], icon: Radio, span: 1 },
    ],
  },
  {
    id: 'security',
    title: '安全 & 生态 · Security & Ecosystem',
    subtitle: '安全启动 · SELinux · ROM · 厂商生态',
    accent: 'text-rose-500',
    icon: Shield,
    cards: [
      // device.security / device.ecosystem are *virtual* cards: they reuse
      // device.build + device.runtime + device.info results that are
      // already fetched by other cards. The dashboard composes them
      // locally rather than spawning extra adb calls.
      { id: 'security', title: '安全启动 · Security', cmds: ['device.build', 'device.runtime'], icon: Shield, span: 2 },
      { id: 'ecosystem', title: '厂商生态 · Ecosystem', cmds: ['device.info'], icon: Zap, span: 1 },
    ],
  },
];

interface QuerySlot {
  cardKey: string;
  cmd: string;
}

export default function Dashboard() {
  const { serial, isOnline, currentState } = useDevice();
  const me = useAuth((s) => s.user);

  // Flat list of (card, cmd) pairs so a single `useQueries` call can fan out
  // every group's commands. We then group by `card.id` to compose each
  // card's merged result and refresh helpers. Multiple cards may share a
  // command (e.g. device.info feeds both 设备 and 厂商生态) — react-query
  // dedupes on queryKey so we don't issue duplicate adb calls.
  const flatQueries = useMemo<QuerySlot[]>(() => {
    const slots: QuerySlot[] = [];
    for (const g of GROUPS) {
      for (const c of g.cards) {
        for (const cmd of c.cmds) {
          slots.push({ cardKey: `${g.id}:${c.id}`, cmd });
        }
      }
    }
    return slots;
  }, []);

  const queries = useQueries({
    queries: flatQueries.map((q) => ({
      queryKey: ['adb.run', serial, q.cmd] as const,
      queryFn: () => adbApi.run({ serial, name: q.cmd }),
      // Allow react-query to keep the last successful snapshot when the
      // device drops offline so each card shows the previous reading
      // alongside the OfflineBanner instead of going blank.
      enabled: !!serial && isOnline,
      staleTime: 30_000,
      gcTime: 10 * 60_000,
    })),
  });

  const cardData = useMemo(() => {
    // Map cardKey → merged data + fetch state. Used by both the rendered
    // cards and the export snapshot builder.
    const out: Record<
      string,
      { data?: Record<string, unknown>; isFetching: boolean; error?: Error; refetch: () => Promise<unknown> }
    > = {};
    for (const g of GROUPS) {
      for (const c of g.cards) {
        const cardKey = `${g.id}:${c.id}`;
        const idxs = flatQueries
          .map((q, i) => (q.cardKey === cardKey ? i : -1))
          .filter((i) => i >= 0);
        const subs = idxs.map((i) => queries[i]);
        out[cardKey] = {
          isFetching: subs.some((q) => q.isFetching),
          error: subs.find((q) => q.error)?.error as Error | undefined,
          data: subs.every((q) => !q.data)
            ? undefined
            : (Object.assign(
                {},
                ...subs.map((q) => (q.data?.result as Record<string, unknown>) ?? {})
              ) as Record<string, unknown>),
          refetch: () => Promise.all(subs.map((q) => q.refetch())),
        };
      }
    }
    return out;
  }, [flatQueries, queries]);

  // Per-group fetching aggregate for the "刷新该组" button + spinner.
  const groupRefetch = (gid: string) => {
    const matching = GROUPS.find((g) => g.id === gid);
    if (!matching) return Promise.resolve();
    return Promise.all(matching.cards.map((c) => cardData[`${gid}:${c.id}`].refetch()));
  };
  const groupFetching = (gid: string): boolean => {
    const matching = GROUPS.find((g) => g.id === gid);
    if (!matching) return false;
    return matching.cards.some((c) => cardData[`${gid}:${c.id}`].isFetching);
  };

  const offlineLabel: Record<typeof currentState, string> = {
    device: '在线',
    offline: '已离线',
    unauthorized: '未授权',
    unknown: '未识别',
  };

  // Snapshot builder for the export button. Re-derived on each click via a
  // closure so the export sees fresh react-query data without us caching it
  // in component state. Cards that share a command (device.info shared by
  // hardware + ecosystem) are emitted under their own card id.
  const buildSnapshot = (): FingerprintSnapshot => {
    const groups: FingerprintSnapshot['groups'] = {};
    for (const g of GROUPS) {
      const cards: Record<string, Record<string, unknown> | undefined> = {};
      for (const c of g.cards) {
        cards[c.id] = cardData[`${g.id}:${c.id}`].data;
      }
      groups[g.id] = cards;
    }
    const info = (cardData['hardware:device']?.data ?? {}) as Record<string, unknown>;
    const build = (cardData['system:build']?.data ?? {}) as Record<string, unknown>;
    return {
      generatedAt: new Date().toISOString(),
      serial: serial ?? '',
      user: me?.username,
      summary: {
        model: pickString(info.model),
        manufacturer: pickString(info.manufacturer),
        release: pickString(info.release),
        sdk: typeof info.sdk === 'number' ? info.sdk : undefined,
        oemSkin: pickString(info.oemSkin),
        oemSkinVersion: pickString(info.oemSkinVersion),
        securityPatch: pickString(build.securityPatch),
      },
      groups,
    };
  };

  return (
    <section className="space-y-5 p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">设备总览</h1>
          <p className="mt-0.5 text-[11px] text-ink-dim">
            实时镜像已移到左侧栏：在「进入中的设备」的设备名旁点
            <Smartphone className="mx-0.5 inline h-3 w-3" />
            图标即可最大化／最小化。所有数据来自 adb-toolbox，敏感字段（IMEI 等）受 Android 隐私限制不会出现。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn"
            onClick={() => queries.forEach((q) => q.refetch())}
            disabled={!serial || !isOnline}
          >
            <RefreshCw className="h-4 w-4" />
            <span>全部刷新</span>
          </button>
          <ExportDeviceFingerprint
            serial={serial ?? ''}
            user={me?.username}
            buildSnapshot={buildSnapshot}
            disabled={!serial}
          />
        </div>
      </header>
      {!serial && (
        <div className="card-pad text-sm text-ink-muted">请在右上角选择一台设备开始。</div>
      )}
      {serial && !isOnline && (
        <div
          className="card-pad flex items-start gap-2 border-amber-500 bg-amber-500/10 text-sm text-amber-600"
          role="alert"
        >
          <WifiOff className="mt-0.5 h-4 w-4" />
          <div className="space-y-1">
            <p className="font-medium">手机已 {offlineLabel[currentState]}，请检查 USB 或重新授权 adb 调试。</p>
            <p className="text-xs text-ink-muted">
              选中的设备 <code className="font-mono">{serial}</code> 不可用；以下卡片显示的是上次的快照。
            </p>
          </div>
        </div>
      )}

      {GROUPS.map((g) => {
        const Icon = g.icon;
        return (
          <GroupSection
            key={g.id}
            id={g.id}
            title={g.title}
            subtitle={g.subtitle}
            count={g.cards.length}
            icon={<Icon className="h-4 w-4" />}
            accent={g.accent}
            isFetching={groupFetching(g.id)}
            onRefresh={() => groupRefetch(g.id)}
            refreshDisabled={!serial || !isOnline}
          >
            {g.cards.map((c) => {
              const CIcon = c.icon;
              const slot = cardData[`${g.id}:${c.id}`];
              const data = c.id === 'security'
                ? composeSecurityCard(slot.data)
                : c.id === 'ecosystem'
                  ? composeEcosystemCard(slot.data)
                  : slot.data;
              const entries = data ? flatten(data) : [];
              // Horizontal span — explicit `c.span` from the GROUPS config
              // wins over the field-count heuristic so authoring stays
              // predictable. Loading / errored cards (no data yet) default
              // to span 1 so the grid doesn't reflow violently when data
              // lands; once we have entries, `pickSpan` (weighted by
              // long-value count) takes over for anything not explicitly
              // declared.
              const span: 1 | 2 | 3 =
                c.span ?? (data ? pickSpan(entries) : 1);
              return (
                <div key={`${g.id}:${c.id}`} className={SPAN_CLASS[span]}>
                  <KvCard
                    title={c.title}
                    icon={<CIcon className={clsx('h-4 w-4', g.accent)} />}
                    action={
                      <button
                        className="btn-ghost"
                        onClick={() => slot.refetch()}
                        disabled={slot.isFetching || !serial || !isOnline}
                        title="刷新"
                      >
                        <RefreshCw className={clsx('h-3.5 w-3.5', slot.isFetching && 'animate-spin')} />
                      </button>
                    }
                  >
                    {slot.error && !data && (
                      <ErrorBubble error={slot.error} />
                    )}
                    {data ? (
                      <KvList entries={entries} columns={span === 1 ? 2 : span === 2 ? 3 : 4} />
                    ) : !slot.error ? (
                      <p className="text-xs text-ink-muted">加载中…</p>
                    ) : null}
                    {c.variant === 'cpu' && serial && (
                      <Link
                        to={`/devices/${encodeURIComponent(serial)}/perf`}
                        className="mt-auto pt-2 inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
                      >
                        <Activity className="h-3 w-3" />
                        打开性能面板（实时图表 / 进程 kill / 录制导出）
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    )}
                    {c.variant === 'top' && serial && (
                      <Link
                        to={`/devices/${encodeURIComponent(serial)}/perf`}
                        className="mt-auto pt-2 inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
                      >
                        <Activity className="h-3 w-3" />
                        在性能面板查看完整进程列表
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    )}
                  </KvCard>
                </div>
              );
            })}
          </GroupSection>
        );
      })}
    </section>
  );
}

/* -------------------------------------------------------------------- */
/* Helpers                                                              */
/* -------------------------------------------------------------------- */

/**
 * Pick a column-span for a card based on the **visual weight** of its
 * flattened entries. Used as the fallback when a `CardSpec` doesn't
 * declare an explicit span. Field count alone is misleading because a
 * single long string (a Wi-Fi rawInfoLine, a long fingerprint) can
 * dwarf 6 short numeric fields visually. We approximate that:
 *
 *   - each entry counts as 1 weighted unit
 *   - any value longer than 60 chars adds another unit (it'll wrap to ≥ 2 lines)
 *   - any value with a newline/comma adds another unit (it forces a full row)
 *
 * Resulting thresholds:
 *
 *   ≤ 8 units  → 1 col   (cellular / bluetooth / dnsProxy)
 *   ≤ 16 units → 2 cols  (cpu / memory / display / battery)
 *   else       → 3 cols  (build / sensors / storage / device.info)
 */
function pickSpan(entries: Array<[string, string]>): 1 | 2 | 3 {
  let units = 0;
  for (const [, v] of entries) {
    units += 1;
    if (v.length > 60) units += 1;
    if (/[\n,，]/.test(v)) units += 1;
  }
  if (units >= 17) return 3;
  if (units >= 9) return 2;
  return 1;
}

/**
 * Tailwind classes for the grid wrapper around each card. Mobile (< md)
 * is always a single column so spans collapse to 1; md is a 2-col grid so
 * we cap span 3 at "all 2 cols"; xl honours the full 1/2/3.
 */
const SPAN_CLASS: Record<1 | 2 | 3, string> = {
  // `h-full` is critical here: CSS Grid's `align-items: stretch` already
  // stretches this wrapper to the row height, but we then need to forward
  // that height into the KvCard inside (which is `flex flex-col h-full`)
  // so the card body matches the row, instead of ending at its content.
  1: 'col-span-1 h-full',
  2: 'col-span-1 md:col-span-2 xl:col-span-2 h-full',
  3: 'col-span-1 md:col-span-2 xl:col-span-3 h-full',
};

/**
 * Friendly fallback for a card whose command failed. Instead of dumping
 * the raw `INTERNAL: adb -s SERIAL shell echo '===DUMP===' ...` payload
 * (which scrolls a card off-screen and confuses non-engineer users), we
 * surface a short summary; the full message stays available via the
 * tooltip / details disclosure for debugging.
 */
function ErrorBubble({ error }: { error: Error }) {
  const msg = error.message || '未知错误';
  // Pull the most-recognisable phrase out of the long INTERNAL line.
  const short =
    /TIMEOUT/i.test(msg)
      ? '采集超时'
      : /unauthorized|UNAUTHORIZED/i.test(msg)
        ? 'adb 未授权'
        : /offline|OFFLINE/i.test(msg)
          ? '设备离线'
          : /not found|UNSUPPORTED/i.test(msg)
            ? '该字段当前 ROM 不支持'
            : '此字段暂不可用';
  return (
    <details className="group cursor-pointer">
      <summary
        className="flex items-center gap-1 text-xs text-amber-600 marker:hidden [&::-webkit-details-marker]:hidden"
        title={msg}
      >
        <span aria-hidden>⚠</span>
        <span>{short}</span>
        <span className="text-[10px] text-ink-dim group-open:hidden">· 点击查看详情</span>
      </summary>
      <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-muted px-2 py-1 font-mono text-[10px] text-ink-muted">
        {msg}
      </pre>
    </details>
  );
}

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * Compose the virtual "安全启动" card from the merged device.build +
 * device.runtime payload (slot.data has both shallow-merged in cmds order).
 * We pick only the security-relevant subset so the card stays focused
 * — full build / runtime data still lives in their own cards.
 */
function composeSecurityCard(data?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!data) return undefined;
  const fields = [
    'verifiedBootState',
    'flashLocked',
    'cryptoState',
    'cryptoType',
    'selinux',
    'usbMode',
    'adbSecure',
    'devOptions',
    'mockLocation',
    'bootReason',
  ];
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (data[f] !== undefined) out[f] = data[f];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * "厂商生态" reuses device.info (which now carries oemSkin + skin version
 * detected client-side based on getprop). The card surfaces only the
 * ecosystem-relevant fields.
 */
function composeEcosystemCard(data?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!data) return undefined;
  const fields = ['oemSkin', 'oemSkinVersion', 'manufacturer', 'brand', 'firstApiLevel', 'fingerprint'];
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (data[f] !== undefined) out[f] = data[f];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * `device.uptime` returns raw seconds since boot. Render as
 * `<月> <天> <时> <分>` so a glance answers "how long has this been up".
 */
function formatUptime(secs: number): string {
  const total = Math.max(0, Math.floor(secs));
  const mo = Math.floor(total / (86400 * 30));
  const d = Math.floor(total / 86400) % 30;
  const h = Math.floor(total / 3600) % 24;
  const m = Math.floor(total / 60) % 60;
  const parts: string[] = [];
  if (mo) parts.push(`${mo} 月`);
  if (d) parts.push(`${d} 天`);
  if (h) parts.push(`${h} 时`);
  parts.push(`${m} 分`);
  return parts.join(' ');
}

/* -------------------------------------------------------------------- */
/* Sensor / camera detail formatters                                    */
/* -------------------------------------------------------------------- */

const FACING_CN: Record<string, string> = {
  front: '前置',
  back: '后置',
  external: '外接',
};

const REPORTING_MODE_CN: Record<string, string> = {
  continuous: '连续',
  'on-change': '变化触发',
  'one-shot': '单次',
  'special-trigger': '专用触发',
};

/**
 * Bucket label inferred from the sensor's name/type. Mirrors the
 * server-side bucketing rules in `parseSensorService` but compresses
 * the long `motion` / `light` etc. labels to single Chinese chars so
 * each detail line stays under ~50 chars.
 */
function classifySensorBucket(s: Record<string, unknown>): string {
  const blob = `${s.name ?? ''} ${s.type ?? ''}`.toLowerCase();
  if (/accelerometer|gyroscope|step|gravity|linear|rotation|game|geomagnetic_rotation|significant_motion|tilt|wake_gesture/.test(blob)) return '运动';
  if (/magnetic|orientation|pose|pickup/.test(blob)) return '姿态';
  if (/barometer|pressure|temperature|humidity|relative_humidity/.test(blob)) return '环境';
  if (/fingerprint|heart|biometric|hrm/.test(blob)) return '生物';
  if (/light|proximity/.test(blob)) return '光线';
  return '其他';
}

/**
 * Build a `<sensor name> · <bucket> · <rate> · <fifo> · <wake>` row that
 * fits one terminal line. Components stay optional — if a sensor has no
 * rate or no FIFO, those segments are simply skipped instead of showing
 * placeholder dashes that just clutter the line.
 */
function formatSensorLine(s: Record<string, unknown>): string {
  const parts: string[] = [];
  const bucket = classifySensorBucket(s);
  const name = String(s.name ?? '');
  parts.push(name);
  parts.push(bucket);
  const minR = typeof s.minRateHz === 'number' ? s.minRateHz : undefined;
  const maxR = typeof s.maxRateHz === 'number' ? s.maxRateHz : undefined;
  if (minR != null && maxR != null && minR !== maxR) parts.push(`${minR}–${maxR} Hz`);
  else if (maxR != null) parts.push(`${maxR} Hz`);
  else if (minR != null) parts.push(`${minR} Hz`);
  const mode = typeof s.reportingMode === 'string' ? REPORTING_MODE_CN[s.reportingMode] : undefined;
  if (mode) parts.push(mode);
  if (typeof s.fifoMax === 'number' && s.fifoMax > 0) parts.push(`FIFO ${s.fifoMax}`);
  if (s.wakeUp === true) parts.push('唤醒源');
  else if (s.wakeUp === false) parts.push('非唤醒');
  if (typeof s.permission === 'string' && s.permission) {
    // Strip `android.permission.` prefix for brevity.
    parts.push(`需 ${(s.permission as string).replace(/^android\.permission\./, '')}`);
  }
  return parts.join(' · ');
}

/**
 * Build a `<id> · <facing> · ⚡ · <res> · <aperture> · <focal> · HAL`
 * row. Picks the most informative subset of fields without exceeding
 * ~80 chars on a typical 4-camera device.
 */
function formatCameraLine(c: Record<string, unknown>): string {
  const parts: string[] = [];
  parts.push(String(c.id ?? '?'));
  if (typeof c.facing === 'string' && FACING_CN[c.facing as string]) {
    parts.push(FACING_CN[c.facing as string]);
  }
  if (c.hasFlash === true) parts.push('⚡');
  // Headline resolution: prefer maxJpegSize (well-defined), fall back to
  // activeArraySize (the cropped imaging area) when the device's HAL
  // didn't expose a JPEG size table.
  const res = (c.maxJpegSize as { width?: number; height?: number } | undefined) ??
    (c.activeArraySize as { width?: number; height?: number } | undefined);
  if (res?.width && res?.height) {
    const mp = (res.width * res.height) / 1_000_000;
    parts.push(`${res.width}×${res.height} (${mp.toFixed(1)}MP)`);
  }
  if (Array.isArray(c.aperturesF) && (c.aperturesF as number[]).length) {
    const apers = (c.aperturesF as number[]).slice(0, 3).map((a) => `f/${a.toFixed(2)}`).join('/');
    parts.push(apers);
  }
  if (Array.isArray(c.focalLengthsMm) && (c.focalLengthsMm as number[]).length) {
    const fls = (c.focalLengthsMm as number[]).slice(0, 3).map((f) => `${f.toFixed(2)}mm`).join('/');
    parts.push(fls);
  }
  if (typeof c.maxDigitalZoom === 'number' && c.maxDigitalZoom > 1) {
    parts.push(`变焦 ${(c.maxDigitalZoom as number).toFixed(0)}×`);
  }
  if (c.physicalSensorSizeMm) {
    const sz = c.physicalSensorSizeMm as { width?: number; height?: number };
    if (sz.width && sz.height) {
      // 1/N" inferred from sensor diagonal in mm — formula: 16 / diag_mm.
      const diag = Math.sqrt(sz.width ** 2 + sz.height ** 2);
      const sensorClass = (16 / diag).toFixed(1);
      parts.push(`1/${sensorClass}寸`);
    }
  }
  if (Array.isArray(c.aeCompensationRange)) {
    const [lo, hi] = c.aeCompensationRange as [number, number];
    if (Number.isFinite(lo) && Number.isFinite(hi)) parts.push(`EV ${lo}~${hi}`);
  }
  if (typeof c.halDeviceVersion === 'string') parts.push(`HAL ${c.halDeviceVersion}`);
  // Capabilities go on a continuation line so the per-camera headline
  // stays scannable. They're shortened to the human-relevant ones.
  const caps = Array.isArray(c.capabilities) ? (c.capabilities as string[]) : [];
  const interesting = caps.filter((cap) =>
    /RAW|MANUAL_SENSOR|BURST_CAPTURE|MOTION_TRACKING|LOGICAL_MULTI_CAMERA|MONOCHROME|YUV_REPROCESSING|PRIVATE_REPROCESSING|HIGH_SPEED|ULTRA_HIGH/.test(cap)
  );
  let line = parts.join(' · ');
  if (interesting.length) line += `\n   能力: ${interesting.join(' / ')}`;
  return line;
}

/**
 * Flatten one level of an object. Nested objects are summarised as `{n keys}`,
 * arrays as `[len]`. Keeps cards skim-friendly even when commands return rich
 * `dumpsys` snapshots; users can use the Commands page for full output.
 *
 * Special-case `uptimeSeconds` -> a synthetic `uptimeReadable` line (the
 * raw number is dropped so the card shows just the human form).
 *
 * `perf.cpuinfo` returns `{ totals: {...}, raw: '...' }`. The `totals`
 * sub-object holds the actual percentages — flattening it inline keeps
 * the CPU card readable instead of showing a useless `{5 keys}` row.
 *
 * `device.sensors` returns `{ count, buckets, sensors[] }`. We expand
 * `buckets` inline (each category becomes its own row) so the card
 * answers "what kinds of sensors does this phone have?" at a glance.
 *
 * `device.storage` returns `{ mounts: [...], partitions: [...], ... }`
 * — we render up to 4 mounts inline as `mount: 50% (xx GB)` rows.
 */
function flatten(obj: Record<string, unknown>): Array<[string, string]> {
  // Whole-object early returns for the "trivially empty" payloads we have
  // historically rendered as a soup of placeholders. perf.thermal in
  // particular returns `{ zones: [], classified: {} }` on devices with no
  // thermal_zone sysfs nodes — that's two empty rows pretending to be
  // useful data. Replace with a single explanatory row so the card looks
  // like a deliberate empty state instead of noise.
  if (isEmptyThermal(obj)) {
    return [['thermal', '暂无温度数据']];
  }

  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(obj)) {
    if (k === 'raw') continue;
    if (k === 'uptimeSeconds' && typeof v === 'number') {
      out.push(['uptimeReadable', formatUptime(v)]);
      continue;
    }
    if (k === 'totals' && v && typeof v === 'object' && !Array.isArray(v)) {
      for (const [tk, tv] of Object.entries(v as Record<string, unknown>)) {
        out.push([tk, tv == null ? '-' : `${tv}%`]);
      }
      continue;
    }
    if (k === 'buckets' && v && typeof v === 'object' && !Array.isArray(v)) {
      for (const [bk, bv] of Object.entries(v as Record<string, unknown>)) {
        out.push([bk, String(bv ?? 0)]);
      }
      continue;
    }
    if (k === 'mounts' && Array.isArray(v)) {
      // Render each mount's used%/free space inline so the storage card
      // tells the user which partition is filling up at a glance.
      let printed = 0;
      for (const m of v as Array<Record<string, unknown>>) {
        if (printed >= 6) break;
        const mount = String(m.mount ?? '');
        if (!mount) continue;
        const pct = typeof m.usedPercent === 'number' ? `${m.usedPercent}%` : '-';
        const sz = typeof m.sizeKb === 'number' ? formatKb(m.sizeKb as number) : '-';
        const free = typeof m.freeKb === 'number' ? formatKb(m.freeKb as number) : '-';
        out.push([mount, `${pct} · 总 ${sz} · 剩 ${free}`]);
        printed++;
      }
      continue;
    }
    if (k === 'sensors' && Array.isArray(v)) {
      // Render one line per sensor with rate / batching / wake-up info,
      // joined by `\n`. KvList's <dd> has `whitespace-pre-wrap`, so
      // newlines render as soft breaks; lists ≥ 200 chars also get
      // auto-collapsed into a <details> there. The summary line shows
      // just `count + first-three names` so the card stays compact when
      // collapsed and reveals the full detail on click.
      const arr = v as Array<Record<string, unknown>>;
      if (arr.length === 0) {
        out.push(['sensorList', '-']);
        continue;
      }
      const detail = arr.map((s) => formatSensorLine(s)).join('\n');
      out.push(['sensorList', detail]);
      continue;
    }
    if (k === 'cameras' && Array.isArray(v)) {
      const arr = v as Array<Record<string, unknown>>;
      if (arr.length === 0) {
        out.push(['cameraList', '-']);
        continue;
      }
      // One row per camera. Format chosen to keep each line ≤ ~70 chars
      // so a 4-camera device fits under the auto-collapse threshold and
      // renders inline, while >= 5 cameras trigger the same <details>
      // fold-out as the sensor list.
      const detail = arr.map((c) => formatCameraLine(c)).join('\n');
      out.push(['cameraList', detail]);
      continue;
    }
    if (k === 'dnsList' && Array.isArray(v)) {
      out.push([k, (v as unknown[]).map(String).join(', ') || '-']);
      continue;
    }
    if (k === 'supportedRefreshRates' && Array.isArray(v)) {
      out.push([k, (v as unknown[]).map((x) => `${x}Hz`).join(' / ') || '-']);
      continue;
    }
    if (k === 'partitions' && Array.isArray(v)) {
      // An empty partitions list shouldn't show a placeholder row (`[0] …`
      // looks like noise next to real data). Skip silently — the card
      // will pick up other storage fields like total size / mounts.
      if ((v as unknown[]).length === 0) continue;
      out.push([k, `[${(v as unknown[]).length}] ${(v as Array<Record<string, unknown>>).slice(0, 3).map((p) => p.name).join(', ')}…`]);
      continue;
    }
    // Cellular `signalStrengthDbm` is reported as INT_MAX (2147483647) when
    // the modem has no signal info — show a dash instead of a 10-digit
    // number that means nothing to a human reader. Same idea for
    // `dataState` ("STATE_-1" / "-1") which TelephonyRegistry emits on
    // modem-less builds.
    if ((k === 'signalStrengthDbm' || k === 'signalLevel') && typeof v === 'number' && (v === 2147483647 || v <= 0)) {
      out.push([k, '-']);
      continue;
    }
    if (k === 'dataState' && (v === 'STATE_-1' || v === '-1' || v === -1)) {
      out.push([k, '-']);
      continue;
    }
    if (v === null || v === undefined) {
      out.push([k, '-']);
    } else if (Array.isArray(v)) {
      out.push([k, summariseList(v)]);
    } else if (typeof v === 'object') {
      const keys = Object.keys(v as Record<string, unknown>);
      out.push([k, `{${keys.length} keys}`]);
    } else if (typeof v === 'boolean') {
      out.push([k, v ? '是' : '否']);
    } else {
      out.push([k, String(v)]);
    }
  }
  return out;
}

/**
 * `perf.thermal` shape on devices without `/sys/class/thermal/thermal_zone*`
 * is `{ zones: [], classified: {} }` — flattening that produces two empty
 * rows ("[0]" + "{0 keys}") that look like a partial result. We treat the
 * whole payload as "no thermal data" and let the caller render a single
 * explanatory row.
 */
function isEmptyThermal(obj: Record<string, unknown>): boolean {
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;
  const onlyThermalKeys = keys.every((k) => k === 'zones' || k === 'classified');
  if (!onlyThermalKeys) return false;
  const zones = obj.zones;
  const classified = obj.classified;
  const zonesEmpty = Array.isArray(zones) ? zones.length === 0 : zones === undefined;
  const classifiedEmpty =
    classified && typeof classified === 'object' && !Array.isArray(classified)
      ? Object.keys(classified as Record<string, unknown>).length === 0
      : classified === undefined;
  return zonesEmpty && classifiedEmpty;
}

function summariseList(v: unknown[]): string {
  if (v.length === 0) return '[]';
  const preview = v
    .slice(0, 3)
    .map((x) => (typeof x === 'object' && x !== null ? '…' : String(x)))
    .join(', ');
  return `[${v.length}] ${preview}${v.length > 3 ? '…' : ''}`;
}

function formatKb(kb: number): string {
  if (!Number.isFinite(kb) || kb <= 0) return '-';
  if (kb < 1024) return `${kb} KB`;
  if (kb < 1024 * 1024) return `${(kb / 1024).toFixed(1)} MB`;
  return `${(kb / 1024 / 1024).toFixed(1)} GB`;
}
